import React, { useMemo } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Home, BarChart2, Users, Heart, MessageCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useCurrentHub } from "@/lib/hooks/use-current-hub";

const NavigationFooter = ({ hubId }: { hubId?: string }) => {
  const [location, navigate] = useLocation();
  const { hubId: resolvedHubId } = useCurrentHub();
  
  // Never fall back to the retired numeric Hub ID. Messenger APIs require the
  // canonical UUID returned by the current-hub resolver.
  const currentHubId = useMemo(() => {
    if (hubId) return hubId;
    return resolvedHubId || '';
  }, [hubId, resolvedHubId]);
  
  // Get unread message count — 30s polling is enough for a badge counter;
  // the messenger page itself handles real-time updates via WebSocket.
  const { data: conversations } = useQuery({
    queryKey: [`/api/messenger/${currentHubId}/conversations`],
    enabled: !!currentHubId,
    refetchInterval: 30000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  // Calculate total unread messages
  const unreadCount = React.useMemo(() => {
    if (!conversations || !Array.isArray(conversations)) return 0;
    return conversations.reduce((total: number, conversation: any) => {
      return total + (conversation.unreadCount || 0);
    }, 0);
  }, [conversations]);
  
  return (
    <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 py-2">
      <div className="container mx-auto flex justify-around">
        <button 
          onClick={() => navigate("/hubs")}
          className="flex flex-col items-center p-2"
        >
          <Home className={location === "/hubs" ? "text-primary" : "text-gray-500"} size={24} />
          <span className={`text-xs mt-1 ${location === "/hubs" ? "text-primary font-medium" : "text-gray-500"}`}>Home</span>
        </button>
        
        <button 
          onClick={() => navigate(`/hub/${currentHubId}/progress`)}
          className="flex flex-col items-center p-2"
        >
          <BarChart2 className={location.includes("/progress") ? "text-primary" : "text-gray-500"} size={24} />
          <span className={`text-xs mt-1 ${location.includes("/progress") ? "text-primary font-medium" : "text-gray-500"}`}>Progress</span>
        </button>

        <button 
          onClick={() => navigate(`/hub/${currentHubId}/health`)}
          className="flex flex-col items-center p-2"
        >
          <Heart className={location.includes("/health") ? "text-primary" : "text-gray-500"} size={24} />
          <span className={`text-xs mt-1 ${location.includes("/health") ? "text-primary font-medium" : "text-gray-500"}`}>Health</span>
        </button>
        
        <button 
          onClick={() => currentHubId ? navigate(`/hub/${currentHubId}/messenger`) : navigate('/hubs')}
          className="flex flex-col items-center p-2 relative"
        >
          <div className="relative">
            <MessageCircle className={location.includes("/messenger") ? "text-primary" : "text-gray-500"} size={24} />
            {unreadCount > 0 && (
              <Badge 
                variant="destructive" 
                className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-xs font-bold min-w-[20px]"
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            )}
          </div>
          <span className={`text-xs mt-1 ${location.includes("/messenger") ? "text-primary font-medium" : "text-gray-500"}`}>Messages</span>
        </button>
        
        <button 
          onClick={() => navigate("/community")}
          className="flex flex-col items-center p-2"
        >
          <Users className={location === "/community" ? "text-primary" : "text-gray-500"} size={24} />
          <span className={`text-xs mt-1 ${location === "/community" ? "text-primary font-medium" : "text-gray-500"}`}>Community</span>
        </button>
      </div>
    </footer>
  );
};

export default NavigationFooter;